import { useIntl } from 'react-intl';

import { formatBytes } from '@xing-com/crate-jobs-domain-apply-helpers';
import type {
  UploadedFile,
  UseUploadResult,
} from '@xing-com/crate-jobs-domain-apply-uploads';
import {
  IconCross,
  IconFile,
  IconFileImage,
  IconFilePDF,
  IconFileWord,
} from '@xing-com/icons';
import { List } from '@xing-com/list';

import {
  BodyCopyContainer,
  IconContainer,
  StyledBodyCopy,
  StyledButton,
  StyledListItem,
  StyledMeta,
  StyledProgressbar,
} from './uploads-list.styles';

type Props = {
  uploads: UploadedFile[];
  onDeleteUpload?: UseUploadResult['handleDeleteUpload'];
};

const ICONS: Record<string, React.FC> = {
  'text/plain': IconFileWord,
  'application/msword': IconFileWord,
  'application/vnd.oasis.opendocument.text': IconFileWord,
  'application/rtf': IconFileWord,
  'application/pdf': IconFilePDF,
  'image/jpeg': IconFileImage,
  'image/png': IconFileImage,
};

export const UploadsList: React.FC<Props> = ({ uploads, onDeleteUpload }) => {
  const { formatMessage } = useIntl();

  return (
    <List>
      {uploads.map((upload) => {
        const { id, fileName, fileType, fileSize, progress } = upload;
        const Icon = ICONS[fileType] ?? IconFile;

        return (
          <StyledListItem key={id} variant="left">
            {progress !== 100 ? (
              <StyledProgressbar
                progress={progress}
                data-testid="uploadProgressBar"
              />
            ) : (
              <>
                <IconContainer>
                  <Icon aria-hidden />
                </IconContainer>

                <BodyCopyContainer>
                  <StyledBodyCopy fontWeight="bold" size="medium" noMargin>
                    {fileName}
                  </StyledBodyCopy>
                  <StyledMeta size="small">
                    {formatBytes(fileSize, 0)}
                  </StyledMeta>
                </BodyCopyContainer>
              </>
            )}

            {onDeleteUpload ? (
              <StyledButton
                icon={IconCross}
                onClick={(): void => {
                  onDeleteUpload(upload);
                }}
                data-testid="deleteUploadButton"
                aria-label={formatMessage(
                  {
                    id: 'JOBS_INSTANT_APPLY_FORM_DELETE_FILE_UPLOAD_BUTTON_A11Y_WEB',
                  },
                  { fileName: fileName }
                )}
              />
            ) : null}
          </StyledListItem>
        );
      })}
    </List>
  );
};
